import React, { useState, useEffect } from 'react';
import { tagsAPI } from '../services/api';
import './TagsManager.css';

const TAG_GUIDANCE_MAX_LENGTH = 5000;
const EMPTY_FORM_DATA = { name: '', description: '', evaluation_approach: '' };

const TagsManager = () => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [activeTagSection, setActiveTagSection] = useState('user');
  const [formData, setFormData] = useState(EMPTY_FORM_DATA);
  const [formErrors, setFormErrors] = useState({});

  const getTagId = (tag) => {
    if (typeof tag === 'string' || typeof tag === 'number') {
      return tag;
    }
    return tag?.id ?? tag?.tag_id ?? tag?.pk ?? null;
  };

  // Load tags on component mount
  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await tagsAPI.getAll();
      setTags(response.results || []);
    } catch (err) {
      setError('Failed to load tags. Please try again.');
      console.error('Error loading tags:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Tag name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Tag name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Tag name must be less than 100 characters';
    }

    if (formData.description.length > TAG_GUIDANCE_MAX_LENGTH) {
      newErrors.description = `Cue generation description must be ${TAG_GUIDANCE_MAX_LENGTH} characters or fewer`;
    }

    if (formData.evaluation_approach.length > TAG_GUIDANCE_MAX_LENGTH) {
      newErrors.evaluation_approach = `Evaluation approach must be ${TAG_GUIDANCE_MAX_LENGTH} characters or fewer`;
    }
    
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getFieldError = (err, fieldName) => {
    const fieldError = err?.[fieldName];
    if (Array.isArray(fieldError)) {
      return fieldError[0];
    }
    return fieldError || '';
  };

  const setServerFormErrors = (err) => {
    const nextErrors = {
      name: getFieldError(err, 'name'),
      description: getFieldError(err, 'description'),
      evaluation_approach: getFieldError(err, 'evaluation_approach')
    };
    const filteredErrors = Object.fromEntries(
      Object.entries(nextErrors).filter(([, value]) => Boolean(value))
    );

    if (Object.keys(filteredErrors).length > 0) {
      setFormErrors(filteredErrors);
      return true;
    }

    return false;
  };

  const handleCreateTag = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setError(null);
      const newTag = await tagsAPI.create(
        formData.name.trim(),
        formData.description.trim(),
        formData.evaluation_approach.trim()
      );
      setTags(prev => [newTag, ...prev]);
      setFormData(EMPTY_FORM_DATA);
      setShowCreateForm(false);
    } catch (err) {
      if (!setServerFormErrors(err)) {
        setError('Failed to create tag. Please try again.');
      }
      console.error('Error creating tag:', err);
    }
  };

  const handleEditTag = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const editingTagId = getTagId(editingTag);
    if (!editingTagId) {
      setError('Cannot update tag: missing tag ID.');
      return;
    }

    try {
      setError(null);
      const updatedTag = await tagsAPI.update(
        editingTagId,
        formData.name.trim(),
        formData.description.trim(),
        formData.evaluation_approach.trim()
      );
      setTags(prev => prev.map(tag => 
        String(getTagId(tag)) === String(editingTagId) ? updatedTag : tag
      ));
      setFormData(EMPTY_FORM_DATA);
      setEditingTag(null);
    } catch (err) {
      if (!setServerFormErrors(err)) {
        setError('Failed to update tag. Please try again.');
      }
      console.error('Error updating tag:', err);
    }
  };

  const handleDeleteTag = async (tag) => {
    const tagId = getTagId(tag);
    if (!tagId) {
      setError('Cannot delete tag: missing tag ID.');
      return;
    }

    if (tag.is_system) {
      setError('System tags are read-only and cannot be deleted.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this tag? This will also delete all lines in this tag.')) {
      return;
    }

    try {
      setError(null);
      await tagsAPI.delete(tagId);
      setTags(prev => prev.filter(existingTag => String(getTagId(existingTag)) !== String(tagId)));
    } catch (err) {
      setError('Failed to delete tag. Please try again.');
      console.error('Error deleting tag:', err);
    }
  };

  const startEdit = (tag) => {
    const tagId = getTagId(tag);
    if (!tagId) {
      setError('Cannot edit tag: missing tag ID.');
      return;
    }

    if (tag.is_system) {
      setError('System tags are read-only and cannot be edited.');
      return;
    }

    setEditingTag({ ...tag, id: tagId });
    setActiveTagSection('user');
    setFormData({
      name: tag.name,
      description: tag.description || '',
      evaluation_approach: tag.evaluation_approach || ''
    });
    setShowCreateForm(false);
    setFormErrors({});
  };

  const cancelEdit = () => {
    setEditingTag(null);
    setFormData(EMPTY_FORM_DATA);
    setFormErrors({});
  };

  const cancelCreate = () => {
    setShowCreateForm(false);
    setFormData(EMPTY_FORM_DATA);
    setFormErrors({});
  };

  const systemTags = tags.filter(tag => Boolean(tag.is_system));
  const userTags = tags.filter(tag => !tag.is_system);

  const renderTagsSection = (sectionTags, isSystemSection) => (
    <section className="tags-section">
      {sectionTags.length === 0 ? (
        <div className="section-empty">
          <p>No {isSystemSection ? 'system' : 'user'} tags found.</p>
        </div>
      ) : (
        <div className="tags-list-container">
          {sectionTags.map(tag => (
            <div key={getTagId(tag) ?? `tag-${tag.name}`} className="tag-item">
              <div className="tag-info">
                <h4 className="tag-name">{tag.name}</h4>
                {tag.description && (
                  <p className="tag-description">
                    <span>Cue generation:</span> {tag.description}
                  </p>
                )}
                {tag.evaluation_approach && (
                  <p className="tag-description tag-evaluation-approach">
                    <span>Evaluation:</span> {tag.evaluation_approach}
                  </p>
                )}
                <p className="tag-meta">
                  {tag.line_count} {tag.line_count === 1 ? 'line' : 'lines'}
                </p>
                <p className="tag-date">
                  Created {new Date(tag.created_at).toLocaleDateString()}
                </p>
              </div>

              {isSystemSection ? (
                <div className="readonly-badge">Read-only</div>
              ) : (
                <div className="tag-actions">
                  <button
                    className="btn btn-small btn-secondary"
                    onClick={() => startEdit(tag)}
                    disabled={showCreateForm}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-small btn-danger"
                    onClick={() => handleDeleteTag(tag)}
                    disabled={showCreateForm}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );

  if (loading) {
    return (
      <div className="tags-manager">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading tags...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tags-manager">
      <div className="tags-header">
        <h2>Tags Management</h2>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="tags-subtabs">
        <div className="tags-subtabs-header" role="tablist" aria-label="Tag sections">
          <button
            type="button"
            role="tab"
            className={`subtab-button ${activeTagSection === 'user' ? 'active' : ''}`}
            aria-selected={activeTagSection === 'user'}
            onClick={() => setActiveTagSection('user')}
          >
            User Tags <span className="section-count">{userTags.length}</span>
          </button>
          <button
            type="button"
            role="tab"
            className={`subtab-button ${activeTagSection === 'system' ? 'active' : ''}`}
            aria-selected={activeTagSection === 'system'}
            onClick={() => setActiveTagSection('system')}
          >
            System Tags <span className="section-count">{systemTags.length}</span>
          </button>
        </div>

        {activeTagSection === 'user' && (
          <>
            <div className="section-actions">
              <button
                className="btn btn-primary"
                onClick={() => setShowCreateForm(true)}
                disabled={editingTag}
              >
                + Add New Tag
              </button>
            </div>

            {(showCreateForm || editingTag) && (
              <div className="tag-form">
                <h3>{editingTag ? 'Edit Tag' : 'Create New Tag'}</h3>
                <form onSubmit={editingTag ? handleEditTag : handleCreateTag}>
                  <div className="form-group">
                    <label htmlFor="name">Tag Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={formErrors.name ? 'error' : ''}
                      placeholder="Enter tag name (e.g., Flirting, State Pumps)"
                      autoFocus
                    />
                    {formErrors.name && (
                      <span className="error-text">{formErrors.name}</span>
                    )}
                  </div>
                  <div className="form-group">
                    <div className="field-header">
                      <label htmlFor="description">Cue generation description (Optional)</label>
                      <span className="character-count">
                        {formData.description.length} / {TAG_GUIDANCE_MAX_LENGTH}
                      </span>
                    </div>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      className={formErrors.description ? 'error' : ''}
                      placeholder="Guidance for generating cues/scenarios for this tag"
                      maxLength={TAG_GUIDANCE_MAX_LENGTH}
                      rows="4"
                    />
                    {formErrors.description && (
                      <span className="error-text">{formErrors.description}</span>
                    )}
                  </div>
                  <div className="form-group">
                    <div className="field-header">
                      <label htmlFor="evaluation_approach">Evaluation approach (Optional)</label>
                      <span className="character-count">
                        {formData.evaluation_approach.length} / {TAG_GUIDANCE_MAX_LENGTH}
                      </span>
                    </div>
                    <textarea
                      id="evaluation_approach"
                      name="evaluation_approach"
                      value={formData.evaluation_approach}
                      onChange={handleInputChange}
                      className={formErrors.evaluation_approach ? 'error' : ''}
                      placeholder="Guidance for evaluating whether the user's answer fits this tag"
                      maxLength={TAG_GUIDANCE_MAX_LENGTH}
                      rows="4"
                    />
                    {formErrors.evaluation_approach && (
                      <span className="error-text">{formErrors.evaluation_approach}</span>
                    )}
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary">
                      {editingTag ? 'Update Tag' : 'Create Tag'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={editingTag ? cancelEdit : cancelCreate}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="tags-list">
              {renderTagsSection(userTags, false)}
            </div>
          </>
        )}

        {activeTagSection === 'system' && (
          <div className="tags-list">
            {renderTagsSection(systemTags, true)}
          </div>
        )}
      </div>
    </div>
  );
};

export default TagsManager;
